import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";
import ModalLanguageSelector from "@modalSrc/ModalLanguageSelector";
import ConfirmModal from "@modals/ConfirmModal";
import { getLanguageAdminLabel, toTranslationLanguageLike } from "@/utils/languageUtils";
import { translateFromEnglishAsync as requestTranslationFromEnglish } from "@/utils/translationService";
import "@/style/sass/_contentEditModal.scss";

type SupportedLanguage = {
  id?: number | string;
  name?: string;
  abbrev?: string;
  ietf?: string;
};

type HelpDetailHtmlEditorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  supportedLanguages: SupportedLanguage[];
  initialLanguageId: string;
  detailContentByLanguageId: Record<string, string>;
  onSave: (updatedDetailContentByLanguageId: Record<string, string>) => void;
  readOnly?: boolean;
  zIndex?: number;
};

function resolveEnglishLanguageId(languages: SupportedLanguage[]): string {
  const fromName = languages.find((lang) => /english/i.test(lang.name || ""));
  if (fromName?.id) return String(fromName.id);
  const fromAbbrev = languages.find((lang) => /^(en|eng)$/i.test((lang.abbrev || "").trim()));
  if (fromAbbrev?.id) return String(fromAbbrev.id);
  if (languages.some((lang) => Number(lang.id) === 1)) return "1";
  return "";
}

function areDraftMapsEqual(
  left: Record<string, string>,
  right: Record<string, string>,
): boolean {
  const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
  for (const key of keys) {
    if ((left?.[key] ?? "") !== (right?.[key] ?? "")) return false;
  }
  return true;
}

export default function HelpDetailHtmlEditorModal({
  isOpen,
  onClose,
  supportedLanguages,
  initialLanguageId,
  detailContentByLanguageId,
  onSave,
  readOnly = false,
  zIndex = 9100,
}: HelpDetailHtmlEditorModalProps) {
  const [selectedLanguageId, setSelectedLanguageId] = useState("");
  const [draftByLanguageId, setDraftByLanguageId] = useState<Record<string, string>>({});
  const [originalDraftByLanguageId, setOriginalDraftByLanguageId] = useState<Record<string, string>>({});
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const snapshot = { ...detailContentByLanguageId };
    setOriginalDraftByLanguageId(snapshot);
    setDraftByLanguageId(snapshot);
    setSelectedLanguageId(initialLanguageId || String(supportedLanguages[0]?.id ?? ""));
    setLeftPanelWidth(50);
    setShowCloseConfirm(false);
    // Snapshot parent content only when the modal opens, not after each save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setShowCloseConfirm(false);
  }, [isOpen]);

  useEffect(() => {
    if (!splitContainerRef.current) return;
    splitContainerRef.current.style.setProperty("--content-edit-left-width", `${leftPanelWidth}%`);
  }, [leftPanelWidth, isOpen, selectedLanguageId]);

  function onContentChange(event: ChangeEvent<HTMLTextAreaElement>) {
    if (!selectedLanguageId) return;
    const nextValue = event.target.value;
    setDraftByLanguageId((prev) => ({
      ...prev,
      [selectedLanguageId]: nextValue,
    }));
  }

  function onSplitterMouseDown(event: ReactMouseEvent<HTMLDivElement>) {
    event.preventDefault();

    const onMouseMove = (moveEvent: MouseEvent) => {
      const container = splitContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const offsetX = moveEvent.clientX - rect.left;
      const percentage = (offsetX / rect.width) * 100;
      setLeftPanelWidth(Math.min(75, Math.max(25, percentage)));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  function hasUnsavedChanges() {
    return !areDraftMapsEqual(originalDraftByLanguageId, draftByLanguageId);
  }

  function closeWithoutSaving() {
    setShowCloseConfirm(false);
    onClose();
  }

  function handleCloseAttempt() {
    if (hasUnsavedChanges()) {
      setShowCloseConfirm(true);
      return;
    }
    closeWithoutSaving();
  }

  function handleSave() {
    onSave(draftByLanguageId);
    setOriginalDraftByLanguageId({ ...draftByLanguageId });
    setShowCloseConfirm(false);
  }

  async function translateFromEnglishAsync() {
    const englishLanguageId = resolveEnglishLanguageId(supportedLanguages);
    if (!selectedLanguageId || !englishLanguageId || selectedLanguageId === englishLanguageId) return;

    const englishContent = (draftByLanguageId[englishLanguageId] ?? "").trim();
    if (!englishContent) {
      toast.error("English content is required before translating.");
      return;
    }

    try {
      setIsTranslating(true);
      const englishLanguage = supportedLanguages.find((lang) => String(lang.id) === englishLanguageId);
      const targetLanguage = supportedLanguages.find((lang) => String(lang.id) === selectedLanguageId);
      const translatedText = await requestTranslationFromEnglish(
        englishContent,
        toTranslationLanguageLike(targetLanguage),
        toTranslationLanguageLike(englishLanguage),
      );

      setDraftByLanguageId((prev) => ({
        ...prev,
        [selectedLanguageId]: translatedText,
      }));
      toast.success("Content translated.");
    } catch (error) {
      console.error("Error translating detailed help HTML:", error);
      toast.error("Error translating content.");
    } finally {
      setIsTranslating(false);
    }
  }

  if (!isOpen) return null;

  const currentDraft = draftByLanguageId[selectedLanguageId] ?? "";
  const currentLanguage = supportedLanguages.find((lang) => String(lang.id) === selectedLanguageId);
  const englishLanguageId = resolveEnglishLanguageId(supportedLanguages);
  const showTranslateButton =
    supportedLanguages.length > 1 &&
    !!selectedLanguageId &&
    !!englishLanguageId &&
    selectedLanguageId !== englishLanguageId;
  const canTranslate =
    showTranslateButton &&
    !!(draftByLanguageId[englishLanguageId] ?? "").trim();

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleCloseAttempt}
      title={readOnly ? "Detailed Help (read-only)" : "HTML Editor - Detailed Help"}
      size="xl"
      className={`contentEditModal sectionHtmlEditorModal${readOnly ? " viewingHistory" : ""}`}
      zIndex={zIndex}
      footer={(
        <>
          <div className="footerLeft" />
          <div className="footerCenter">
            {supportedLanguages.length > 0 ? (
              <ModalLanguageSelector
                selectId="helpDetailHtmlEditorLanguageSelect"
                languages={supportedLanguages}
                value={selectedLanguageId}
                onChange={setSelectedLanguageId}
                placement="footer"
                getOptionLabel={getLanguageAdminLabel}
              />
            ) : null}
            {showTranslateButton && !readOnly ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void translateFromEnglishAsync()}
                disabled={!canTranslate || isTranslating}
                title={
                  canTranslate
                    ? `Translate English content to ${currentLanguage ? getLanguageAdminLabel(currentLanguage) : "selected language"}`
                    : "English content is required before translating"
                }
              >
                {isTranslating ? "Translating..." : "Translate"}
              </button>
            ) : null}
          </div>
          <div className="footerRight">
            {!readOnly ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!hasUnsavedChanges()}
              >
                Accept
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={handleCloseAttempt}>
              {readOnly ? "Close" : hasUnsavedChanges() ? "Discard Changes" : "Close"}
            </button>
          </div>
        </>
      )}
    >
      <p className="helpDetailHtmlEditorHint">
        This HTML is shown on the detailed help page opened with Ctrl+click on the help element. It is independent of the sticky-note content above.
      </p>
      <div ref={splitContainerRef} className="contentEditGrid active">
        <div className="contentEditSection contentEditSectionRaw">
          <h4>HTML Content ({currentLanguage ? getLanguageAdminLabel(currentLanguage) : selectedLanguageId})</h4>
          <textarea
            className="rawContent sectionEditorHtmlTextarea"
            value={currentDraft}
            readOnly={readOnly}
            onChange={onContentChange}
            placeholder="Edit detailed help HTML here..."
          />
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize editor and preview panels"
          onMouseDown={onSplitterMouseDown}
          className="contentEditSplitter"
        />

        <div className="contentEditSection contentEditSectionPreview">
          <h4>Rendered Preview</h4>
          <div className="renderedContent">{parseSanitizedHtml(currentDraft)}</div>
        </div>
      </div>
    </Modal>

    <ConfirmModal
      isOpen={showCloseConfirm}
      onConfirm={closeWithoutSaving}
      onCancel={() => setShowCloseConfirm(false)}
      title="Unsaved Changes"
      message="You have unsaved HTML changes. Are you sure you want to close without saving?"
      confirmText="Yes, Close Without Saving"
      zIndex={zIndex + 1}
    />
    </>
  );
}
